/**
 * The range label of the planner as a date picker (#2038): month and year dropdowns, so the
 * planner jumps to any week or month in two clicks.
 */
import { useState } from 'react';
import { useLingui } from '@lingui/react';
import { CalendarDays } from 'lucide-react';
import { addYears, subYears } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface PlannerRangePickerProps {
  label: string;
  value: Date;
  onChange: (date: Date) => void;
}

export function PlannerRangePicker({ label, value, onChange }: PlannerRangePickerProps) {
  const { i18n } = useLingui();
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-10 flex-1 justify-center gap-2 whitespace-nowrap px-4 text-sm font-medium"
          aria-label={i18n._({
            id: 'shifts.rangePicker.label',
            message: 'Choose a date: {range}',
            values: { range: label },
          })}
          data-testid="planner-range-picker"
        >
          <CalendarDays className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          captionLayout="dropdown"
          startMonth={subYears(value, 2)}
          endMonth={addYears(value, 2)}
          selected={value}
          defaultMonth={value}
          onSelect={(date) => {
            if (date) {
              onChange(date);
              setOpen(false);
            }
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
